'use client';

import type { ReactNode } from 'react';
import { useTranslation } from 'next-i18next/pages';

import type { ButtonVariant } from '@highlight-cp/core-ui';
import { Box, Button, Flex, ModalFooter } from '@highlight-cp/core-ui';

export interface ModalButtonsProps {
  /** Disables the cancel button */
  cancelDisabled?: boolean;
  /** Cancel button label @default 'Cancel' */
  cancelText?: string;
  /** Cancel button variant @default 'default' */
  cancelVariant?: ButtonVariant;
  /** Focuses the confirm button as the modal opens, for a modal whose one action it is */
  confirmAutoFocus?: boolean;
  /** Disables the confirm button */
  confirmDisabled?: boolean;
  /** Confirm button `data-testid` @default 'modal.generic.confirm-button' */
  confirmTestId?: string;
  /** Confirm button label @default 'Save' */
  confirmText?: string;
  /** An action of its own at the start of the row, away from Cancel/Save, such as a red Remove */
  leftSection?: ReactNode;
  /** Shows a spinner in the confirm button */
  loading?: boolean;
  /** Cancel handler, for a cancel that doesn't dismiss, such as a draft's revert @default onClose */
  onCancel?: () => void;
  /** Dismiss handler, wired to the cancel button unless `onCancel` is given */
  onClose?: () => void;
  /** Confirm handler. Closing on success is the caller's job. */
  onConfirm?: () => void | Promise<void>;
  /** @default true */
  withCancelButton?: boolean;
  /** @default true */
  withConfirmButton?: boolean;
}

/**
 * ### ModalButtons
 *
 * @description
 * The Cancel/Save row for a modal footer. Prefer `ActionModal`, which pairs it with core-ui's
 * `Modal` for you. Reach for `ModalButtons` directly when the footer can't just sit at the bottom of
 * an `ActionModal`: a body rendered as a separate component inside someone else's `Modal`, or a
 * footer that appears conditionally.
 *
 * Wherever it sits in the tree, the row renders into the modal's fixed action row (see
 * `ModalFooter`), so a footer several components deep still stays put while the body scrolls.
 *
 * Neither button closes anything on its own: `onCancel` (or else `onClose`) runs on cancel,
 * `onConfirm` on confirm, and dismissing the modal stays with whoever owns its `opened` state.
 *
 * Both are explicitly `type="button"`: several call sites render this inside a `<form>` that has no
 * `onSubmit`, where a default submit button makes Enter (or a click) trigger a native GET submission
 * and navigate away from the page. In a modal the portal lifts the row out of that `<form>` anyway,
 * but the inline path still sits inside it.
 *
 * @example
 * ```tsx
 * <ModalButtons onClose={close} onConfirm={handleSave} confirmText={t('Add Insert')} />
 * ```
 *
 * @param props - {@link ModalButtonsProps}
 * @returns React component
 */
export const ModalButtons = (props: ModalButtonsProps) => {
  const {
    cancelDisabled = false,
    cancelText,
    cancelVariant = 'default',
    confirmAutoFocus,
    confirmDisabled = false,
    confirmTestId = 'modal.generic.confirm-button',
    confirmText,
    leftSection,
    loading = false,
    onCancel,
    onClose,
    onConfirm,
    withCancelButton = true,
    withConfirmButton = true,
  } = props;

  const { t } = useTranslation();

  return (
    <ModalFooter>
      <Flex gap="xs" justify="flex-end">
        {leftSection && <Box mr="auto">{leftSection}</Box>}

        {withCancelButton && (
          <Button
            type="button"
            data-testid="modal.generic.cancel-button"
            variant={cancelVariant}
            disabled={cancelDisabled}
            onClick={onCancel ?? onClose}
          >
            {cancelText ?? t('Cancel')}
          </Button>
        )}

        {withConfirmButton && (
          <Button
            type="button"
            data-autofocus={confirmAutoFocus || undefined}
            data-testid={confirmTestId}
            loading={loading}
            disabled={confirmDisabled}
            onClick={() => void onConfirm?.()}
          >
            {confirmText ?? t('Save')}
          </Button>
        )}
      </Flex>
    </ModalFooter>
  );
};
